import { useState } from "react";
import { ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { FulfilmentIndexKey } from "../api";
import { channelLeaf, label, resolveForRender, toggle, tokens } from "./channelKeys";

const unique = (values: string[]) => [...new Set(values)].sort((a, b) => a.localeCompare(b));

/** A click's next selection: the raw entries are split into their comma tokens, only the tokens the loaded leaves hold reach toggle, so a stale URL selector never throws; with none, the click picks its own key. */
export function pickChannel(raw: string[], key: string, leaves: readonly string[]): string[] {
  const live = tokens(raw).filter((selector) => resolveForRender([selector], leaves).length > 0);
  return live.length > 0 ? toggle(live, key, leaves) : [key];
}

/** The keys route is SKU × place; the picker is deliberately one row per place. */
export function channelPlaces(rows: FulfilmentIndexKey[]): FulfilmentIndexKey[] {
  return [...new Map(rows.map((row) => [channelLeaf(row), row])).values()];
}

export function ChannelPicker({ rows, value, onChange, disabled = false }: {
  rows: FulfilmentIndexKey[]; value: string[]; onChange: (value: string[]) => void; disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const places = channelPlaces(rows);
  const leaves = unique(places.map(channelLeaf));
  const raw = value.length ? value : ["all"];
  const resolved = new Set(resolveForRender(raw, leaves));
  const caption = raw.includes("all") ? "All channels" : raw.length === 1 ? label(raw[0], rows) : `${raw.length} selections`;
  const groups = unique(places.map((row) => row.channel.toLowerCase()));
  const explicit = (key: string) => raw.includes(key);
  const checked = (key: string) => leaves.filter((leaf) => key === "all" || leaf === key || leaf.startsWith(`${key}:`)).every((leaf) => resolved.has(leaf));
  const implied = (key: string) => checked(key) && !explicit(key);
  // An implied checkbox stays inert; selecting its row narrows the parent selection to that branch.
  const set = (key: string) => onChange(implied(key) ? [key] : pickChannel(raw, key, leaves));

  const item = (key: string, depth: number, text = label(key, rows)) => <CommandItem
    key={key}
    value={`${text} ${key}`}
    onSelect={() => set(key)}
    className={depth === 0 ? "font-semibold" : depth === 1 ? "pl-7 font-medium" : "pl-12"}
  >
    <Checkbox data-nonmutating checked={checked(key)} disabled={implied(key)} tabIndex={-1} aria-label={text} className="pointer-events-none" />
    <span>{text}</span>
  </CommandItem>;

  return <div className="grid w-56 max-w-full gap-1.5">
    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Channels</span>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><Button data-nonmutating type="button" variant="outline" role="combobox" aria-label="Channels" aria-expanded={open} disabled={disabled} className="justify-between font-normal"><span className="truncate">{caption}</span><ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" /></Button></PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command><CommandInput data-nonmutating placeholder="Search channels" /><CommandList><CommandEmpty>No matching channel.</CommandEmpty>
          {item("all", 0)}
          {groups.map((group) => <div key={group}>
            {item(group, 0)}
            {unique(places.filter((row) => row.channel.toLowerCase() === group).map((row) => row.region.toLowerCase())).map((region) => {
              const regionKey = `${group}:${region}`;
              return <div key={regionKey}>{item(regionKey, 1)}{places.filter((row) => channelLeaf(row).startsWith(`${regionKey}:`)).sort((a, b) => a.place_label.localeCompare(b.place_label)).map((row) => item(channelLeaf(row), 2, row.place_label))}</div>;
            })}
          </div>)}
        </CommandList></Command>
      </PopoverContent>
    </Popover>
  </div>;
}
